import React, { useMemo } from 'react';

// --- MUI Components ---
import { 
    Box, 
    Typography, 
    IconButton, 
    Stack,
    Chip,
    Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Grid from "../ContainerGrid"

// --- Helper Status Color ---
const getStatusColor = (status) => {
    switch (String(status).toLowerCase()) {
        case 'success': case 'completed': return { bg: 'success.lighter', text: 'success.main' };
        case 'running': case 'processing': return { bg: 'info.lighter', text: 'info.main' };
        case 'failed': case 'error': return { bg: 'error.lighter', text: 'error.main' };
        default: return { bg: 'action.hover', text: 'text.secondary' };
    }
};

// --- Compact JsonViewer Component for Horizontal View ---
const JsonViewer = ({ data }) => {
    if (data === null) return <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '12px' }}>null</Box>;
    if (data === undefined) return <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '12px' }}>undefined</Box>;
    
    // Nếu là object hoặc array phức tạp, hiển thị dạng stringify thu gọn để tiết kiệm không gian nằm ngang
    if (typeof data === 'object') {
        return (
            <Box 
                component="pre" 
                sx={{ 
                    m: 0, 
                    fontFamily: 'monospace', 
                    fontSize: '12px', 
                    color: 'grey.300',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all'
                }}
            >
                {JSON.stringify(data, null, 2)}
            </Box>
        );
    }

    let textColor = 'grey.300';
    if (typeof data === 'string') textColor = 'success.light';
    if (typeof data === 'number') textColor = 'secondary.light';
    if (typeof data === 'boolean') textColor = 'warning.light';

    return (
        <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '12px', color: textColor }}>
            {typeof data === 'string' ? `"${data}"` : String(data)}
        </Box>
    );
};

// --- Main Redesigned NodeStatusBar Component ---
const NodeStatusBar = ({ selectedNode, onClose }) => {
    const nodeRuntimeState = useMemo(() => {
        return ((selectedNode || {}).data || {}).nodeState || {
            status: ((selectedNode || {}).data || {}).status,
            inputPayload: ((selectedNode || {}).data || {}).inputPayload,
            outputData: ((selectedNode || {}).data || {}).outputData,
            error: ((selectedNode || {}).data || {}).error
        };
    }, [selectedNode]);

    if (!selectedNode) return null;

    const { id, data: nodeData } = selectedNode;
    const nodeName = (nodeData || {}).name || id;
    const statusTheme = getStatusColor(nodeRuntimeState.status);

    return (
        <Box 
            className="NodeStatusBar" 
            sx={{ 
                width: '100%', 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                bgcolor: 'background.paper',
                overflow: 'hidden'
            }}
        >
            {/* 1. COMPACT HEADER (Chiếm không gian tối thiểu) */}
            <Box 
                sx={{ 
                    px: 2, 
                    py: 1, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    borderBottom: '1px solid', 
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                    flexShrink: 0
                }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }} noWrap>
                        {nodeName}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                        ({id})
                    </Typography>
                    <Chip 
                        label={nodeRuntimeState.status || 'N/A'} 
                        size="small" 
                        sx={{ 
                            height: 20, 
                            fontSize: '11px', 
                            fontWeight: 600,
                            bgcolor: statusTheme.bg, 
                            color: statusTheme.text 
                        }} 
                    />
                </Stack>
                
                {onClose && (
                    <IconButton onClick={onClose} size="small" edge="end">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>

            {/* 2. THREE-COLUMN HORIZONTAL BODY (Không cần scroll dọc) */}
            <Box sx={{ flexGrow: 1, overflow: 'hidden', p: 1.5, bgcolor: '#1e1e1e' }}>
                <Grid container spacing={1.5} sx={{ height: '100%' }}>
                    
                    {/* CỘT 1: INPUT PAYLOAD */}
                    <Grid size={{xs:4}}  sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="caption" sx={{ color: 'grey.400', fontWeight: 600, mb: 0.5, display: 'block',flexShrink: 0 }}>
                            ĐẦU VÀO (inputPayload)
                        </Typography>
                        <Box 
                            sx={{ 
                                flex: 1, 
                                bgcolor: 'rgba(255,255,255,0.03)', 
                                border: '1px solid rgba(255,255,255,0.08)',
                                borderRadius: 1, 
                                p: 1, 
                                overflow: 'auto' 
                            }}
                        >
                            <JsonViewer data={nodeRuntimeState.inputPayload} />
                        </Box>
                    </Grid>

                    {/* CỘT 2: OUTPUT DATA */}
                    <Grid size={{xs:4}} sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                            <ArrowForwardIcon sx={{ color: 'grey.600', fontSize: 14 }} />
                            <Typography variant="caption" sx={{ color: 'grey.400', fontWeight: 600, display: 'block',flexShrink: 0 }}>
                                ĐẦU RA (outputData)
                            </Typography>
                        </Stack>
                        <Box 
                            sx={{ 
                                flex: 1, 
                                bgcolor: 'rgba(255,255,255,0.03)', 
                                border: '1px solid rgba(255,255,255,0.08)',
                                borderRadius: 1, 
                                p: 1, 
                                overflow: 'auto' 
                            }}
                        >
                            <JsonViewer data={nodeRuntimeState.outputData} />
                        </Box>
                    </Grid>

                    {/* CỘT 3: ERROR LOGS */}
                    <Grid size={{xs:4}} sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                color: nodeRuntimeState.error ? 'error.light' : 'grey.400', 
                                fontWeight: 600, 
                                mb: 0.5, 
                                display: 'block',
                                flexShrink: 0
                            }}
                        >
                            LỖI TRONG NODE (error)
                        </Typography>
                        <Box 
                            sx={{ 
                                flex: 1, 
                                bgcolor: nodeRuntimeState.error ? 'rgba(211, 47, 47, 0.1)' : 'rgba(255,255,255,0.03)', 
                                border: '1px solid',
                                borderColor: nodeRuntimeState.error ? 'rgba(211, 47, 47, 0.25)' : 'rgba(255,255,255,0.08)',
                                borderRadius: 1, 
                                p: 1, 
                                overflow: 'auto' 
                            }}
                        >
                            {nodeRuntimeState.error ? (
                                <JsonViewer data={nodeRuntimeState.error} />
                            ) : (
                                <Typography variant="caption" sx={{ color: 'grey.600', fontStyle: 'italic', display: 'block', p: 0.5 }}>
                                    Không phát hiện lỗi xảy ra.
                                </Typography>
                            )}
                        </Box>
                    </Grid>

                </Grid>
            </Box>
        </Box>
    );
};

export default NodeStatusBar;